// Typ: FindCentralFromInscribedAngleTask
// Podtemat: Kąty i styczne → Kąt środkowy z podanego kąta wpisanego
// Zależność: α_sr = 2 · α_w

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
} from "../../../../components/inputs/FractionInput";
import CircleAnglesDiagram from "../../../../components/diagrams/CircleAnglesDiagram";

/* ===== helpers ===== */

const DEC_TOL = 0.1; // ±0.1° akceptujemy też wartości niecałkowite
function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function pretty(x) {
  const s = String(x ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}

/* ===== generator ===== */

export function generateTask() {
  // wybieramy α_sr (ładne wartości), ale max 300°
  const A15 = Array.from({ length: 23 }, (_, i) => 15 * (i + 1));  // 15..345
  const A10 = Array.from({ length: 34 }, (_, i) => 10 * (i + 2));  // 20..350
  const A40 = Array.from({ length: 8  }, (_, i) => 40 * (i + 1));  // 40..320

  const ALL = [...new Set([...A15, ...A10, ...A40])]
    .filter(a => a !== 360 && a <= 300); // ⟵ limit 300°

  const alpha_sr = ALL[Math.floor(Math.random() * ALL.length)];
  const alpha_w  = alpha_sr / 2;

  return { type: "FindCentralFromInscribedAngleTask", data: { alpha_sr, alpha_w } };
}

/* ===== treść ===== */

export function renderTask({ data }) {
  const { alpha_w } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Dany jest kąt wpisany{" "}
      <InlineMath math={`\\alpha_{w}=${alpha_w}^{\\circ}`} />.
      Oblicz kąt środkowy oparty na tym samym łuku{" "}
      <span style={{ whiteSpace: "nowrap" }}><InlineMath math={`\\alpha_{\\text{?r}}`} />{"."}</span>
    </div>
  );
}

/* ===== input ===== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { raw: "" };
  return (
    <FractionInput
      value={val}
      onChange={(v) => onChange(v)}
      disabled={disabled}
      label="α_sr"        // pokaże się w podglądzie i "Twoja odpowiedź"
      suffixLatex="°"     // zwykły znak stopnia — stabilny podgląd
      placeholder="np. 190 lub 37.5"
    />
  );
}

/* ===== check ===== */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (raw == null || String(raw).trim() === "") return false;

  const val = parseFraction(preprocessRaw(raw));
  if (!Number.isFinite(val)) return false;

  const expected = data.alpha_sr;
  return Math.abs(val - expected) <= DEC_TOL;
}

/* ===== answer (krótko) ===== */

export function renderAnswer({ data }) {
  const { alpha_sr } = data;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\alpha_{\\text{śr}} = ${alpha_sr}^{\\circ}`} />
    </div>
  );
}

/* ===== explanation ===== */

export function renderExplanation({ data }) {
  const { alpha_sr, alpha_w } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór:</div>
      <BlockMath math={'\\alpha_{\\text{śr}} = 2\\,\\alpha_{w}'} />

      <div><strong>Krok 2.</strong> Podstawienie:</div>
      <BlockMath math={`\\alpha_{\\text{śr}} = 2\\cdot ${alpha_w}^{\\circ} = ${alpha_sr}^{\\circ}`} />

      {/* podpis z lewej + wycentrowany diagram */}
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div style={{ alignSelf: "stretch", textAlign: "left", color: "currentColor" }}>
          Wizualizacja:
        </div>
        <CircleAnglesDiagram centralDeg={alpha_sr} centralTone="unknown" inscribedTone="given" />
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`\\displaystyle \\alpha_{\\text{śr}} = ${pretty(raw)}^\\circ`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
